import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Menu,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { EmptyContent, enqueueSnackbar, FormProvider, RHFRadioGroup, Spinner, TableHeadCustom, TableNoData } from '@zeta-core';
import { EyeIcon } from '@zeta-core/assets';
import { AIInteractionService, getErrorMessage, IMergeClientDataRequest } from '@zeta-services';
import { IGenericErrorResponse } from '@zeta-types';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';

interface IConflictsDialog {
  open: boolean;
  handleClose: () => void;
  tableData: any[];
  isPending: boolean;
  title: string;
  refetch?: () => void;
}

export const ConflictsDialog = ({ open, handleClose, tableData, isPending, title, refetch }: IConflictsDialog) => {
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const clientID = location?.state?.clientData?.clientID;

  const handleRowClick = (row: any, e: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedRow(row);
    setAnchorElement(e.currentTarget);
  };

  const handleCloseDialog = () => {
    setSelectedRow(null);
    handleClose();
  };

  function getSubsectionDescription(arr: string[], indexArray: string[]): string {
    if (arr.length <= 1) return '';

    const subItems = arr.slice(1); // Skip first item
    const lastIndex = subItems.length - 1;

    return subItems.map((item, index) => (index === lastIndex ? `${item}${indexArray?.[0] ? ` - ${indexArray[0]}` : ''}` : item)).join(' --> ');
  }

  function getClientTypeLabel(type: string): string {
    switch (type.toLowerCase()) {
      case 'client':
        return 'Primary Client';
      case 'partner':
        return 'Partner Client';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
  }

  const methods = useForm({
    defaultValues: {
      acceptedAuditId: '',
    },
  });

  const acceptedAuditId = methods.watch('acceptedAuditId');

  const mergeClientDataApi = useMutation({
    mutationFn: (payload: IMergeClientDataRequest) => AIInteractionService().mergeClientData(payload),

    onSuccess: ({ data: response }) => {
      enqueueSnackbar('Conflict Resolved Successfully.', { variant: 'success' });
      refetch?.();
      setAnchorElement(null);
    },

    onError: (error: IGenericErrorResponse) => {
      const status = error?.response?.status;
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error?.[0]?.errorCode;

      if (errorCode) {
        enqueueSnackbar(getErrorMessage(errorCode, error?.response?.data?.error), { variant: 'error' });
      } else {
        const fallbackByStatus: Record<number, string> = {
          404: 'No conflict found for the selected record.',
          500: 'Failed to resolve conflict for the selected record.',
        };

        const message = fallbackByStatus[status!] || 'An unexpected error occurred. Please try again.';
        enqueueSnackbar(message, { variant: 'error' });
      }
    },
  });

  const onSubmit = (data: any) => {
    const payload: any = {
      clientId: clientID,
      acceptedAuditId: '',
      rejectedAuditId: '',
    };

    const auditId = selectedRow?.details?.[0]?.auditId || '';

    if (acceptedAuditId === 'new') {
      payload.acceptedAuditId = auditId;
    } else if (acceptedAuditId === 'previous') {
      payload.rejectedAuditId = auditId;
    }

    mergeClientDataApi.mutateAsync(payload);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xl"
      onClose={() => {
        handleCloseDialog();
      }}
      slotProps={{
        paper: {
          sx: {
            height: '75vh', // Set height to 70% of the viewport
            display: 'flex',
            flexDirection: 'column',
          },
        }
      }}
    >
      <Stack pt={3} px={3} mb={2}>
        <Typography variant="h5">{`${title}`} </Typography>
        <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
          Conflicts were detected in this section. Please review and update the values.
        </Typography>
      </Stack>
      <DialogContent>
        <TableContainer sx={{ overflow: 'auto' }}>
          <Table>
            <TableHeadCustom
              headLabel={[
                { id: 'label', label: 'Field Name', show: true, width: 300 },
                { id: 'description', label: 'Description', show: true, width: 800 },
                { id: 'relatedTo', label: 'Related To', show: true },
                { id: 'action', label: 'Action', show: true, width: 100 },
              ]}
            />

            <TableBody>
              {isPending && (
                <TableRow>
                  <TableCell colSpan={2}>
                    <EmptyContent title="Loading..." icon={<Spinner />} />
                  </TableCell>
                </TableRow>
              )}
              {tableData?.length > 0 &&
                tableData.map((row) => (
                  <TableRow key={row.field}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>{getSubsectionDescription(row.subsection, row.index)}</TableCell>
                    <TableCell>{getClientTypeLabel(row.relatesTo?.ownerType)}</TableCell>

                    <TableCell>
                      <IconButton color="primary" onClick={(e) => handleRowClick(row, e)}>
                        <EyeIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

              {tableData?.length === 0 && !isPending && <TableNoData notFound />}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => handleCloseDialog()}>
          Cancel
        </Button>
      </DialogActions>
      <Menu
        id="menu"
        aria-labelledby="menu-button"
        onClose={() => {
          // setSelectedRow(null);
          methods.reset({});
          setAnchorElement(null);
        }}
        open={!!anchorElement}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 500,
              overflowY: 'auto',
              p: 2,
            },
          },
        }}
      >
        <Stack p={2}>
          <Stack gap={0.5} mb={1}>
            <Typography variant="h6">{selectedRow?.label}</Typography>
            <Typography variant="body2">Select the correct information to avoid conflicts</Typography>
          </Stack>

          <Stack>
            <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
              <Stack>
                <RHFRadioGroup
                  name="acceptedAuditId"
                  options={
                    selectedRow?.details?.[0]
                      ? [
                          {
                            label: (
                              <Stack ml={2}>
                                <Typography variant="body2">Previous Value</Typography>
                                <Typography variant="subtitle2">{selectedRow.details[0].previousValue || '-'}</Typography>
                                <Divider sx={{ borderStyle: 'dashed', my: 1, width: '100%' }} />
                              </Stack>
                            ),
                            value: 'previous',
                          },
                          {
                            label: (
                              <Stack ml={2}>
                                <Typography variant="body2">New Value</Typography>
                                <Typography variant="subtitle2">{selectedRow.details[0].newValue || '-'}</Typography>
                                <Divider sx={{ borderStyle: 'dashed', my: 1, width: '100%' }} />
                              </Stack>
                            ),
                            value: 'new',
                          },
                        ]
                      : []
                  }
                  sx={{ ml: -2 }}
                />
              </Stack>

              <Box display="flex" justifyContent="flex-end" mt={2}>
                <LoadingButton variant="contained" type="submit" disabled={!acceptedAuditId}>
                  Update
                </LoadingButton>
              </Box>
            </FormProvider>
          </Stack>
        </Stack>
      </Menu>
    </Dialog>
  );
};
